import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { IAnswers } from 'src/redux/features/surveys/takingSurvey/types';
import { IPutAnswers } from 'src/redux/features/surveys/putTakingSurvey/types';
import { QuestionTypeEnum } from 'src/utils/enum';
import cn from 'classnames/bind';
import styles from './styles.styl';
import { TextField } from '@consta/uikit/TextField';
import { Checkbox } from '@consta/uikit/Checkbox';
import { Radio } from '@consta/uikit/Radio';
import { ITextField } from 'src/components/TakingSurveyContent/index';
import { TakingSurveyEnum } from 'src/utils/enum';
import { ChoiceGroup } from '@consta/uikit/ChoiceGroup';
import { Text } from '@consta/uikit/Text';
import { CustomCheckboxGroup } from '../CustomCheckboxGroup';
import { CustomRadioGroup } from '../CustomRadioGroup';

const cx = cn.bind(styles);

export interface AnswersBuildProps {
  type: number;
  answers: IAnswers[];
  question_id: number;
  rating: number;
  changeAnswers: (id: number, chosenAnswers?: IPutAnswers[]) => void;
}

export interface StateValue {
  name: null | string;
  id: number | null;
}

type MultiType = {
  name: null | string;
  id: number | null;
  rating: null | number;
};

type StateMultiRatingType = {
  [id: number]: MultiType;
};

const Answers: React.FC<AnswersBuildProps> = ({
  type,
  answers,
  question_id,
  rating,
  changeAnswers,
}) => {
  const [stateValue, setStateValue] = useState<StateValue[] | null>(null);
  const [fieldValue, setFieldValue] = useState<string | null>(null);
  const [textFieldOpen, setTextFieldOpen] = useState<boolean>(false);
  const [multiRating, setMultiRating] = useState<StateMultiRatingType>(
    answers.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: { id: item.id, name: null, rating: null },
      }),
      {},
    ),
  );
  const [oneRating, setOneRating] = useState<number | null>(null);

  const isMobile: boolean = useMediaQuery({ query: '(max-width: 424px)' });

  useEffect(() => {
    if (type === QuestionTypeEnum.SELF_SELECT) {
      setStateValue(answers.map(({ id }) => ({ id, name: null })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (type === QuestionTypeEnum.MULTIPLE_POINT_RATING && multiRating) {
      const answers = Object.values(multiRating).map(({ id, name, rating }) => {
        return {
          question_answer_id: id,
          value: name,
          rating: rating,
        };
      });
      changeAnswers(question_id, answers);
    } else if (type === QuestionTypeEnum.POINT_RATING && oneRating) {
      const answers: IPutAnswers[] = [
        { question_answer_id: null, value: String(oneRating) },
      ];
      changeAnswers(question_id, answers);
    } else if (!stateValue) {
      changeAnswers(question_id);
    } else if (type === QuestionTypeEnum.SINGLE_SELECT) {
      const answers = [
        {
          question_answer_id: stateValue[0].id,
          value: stateValue[0].name,
        },
      ];
      changeAnswers(question_id, answers);
    } else if (type === QuestionTypeEnum.SINGLE_AND_SELF) {
      const value = stateValue[0].name;
      if (value !== TakingSurveyEnum.SINGLE_SELF_CHOICE) {
        const answers = [
          {
            question_answer_id: stateValue[0].id,
            value,
          },
        ];
        changeAnswers(question_id, answers);
      } else {
        changeAnswers(question_id);
      }
    } else if (type === QuestionTypeEnum.SELF_SELECT) {
      const answers = stateValue.map(({ name }) => {
        return {
          question_answer_id: null,
          value: name,
        };
      });
      changeAnswers(question_id, answers);
    } else if (type === QuestionTypeEnum.MULTIPLE_AND_SELF) {
      const answers = stateValue.map(({ name, id }) => {
        return {
          question_answer_id: id,
          value: name,
        };
      });
      if (isMobile && textFieldOpen && !fieldValue) {
        changeAnswers(question_id);
      } else changeAnswers(question_id, answers);
    } else if (type === QuestionTypeEnum.MULTIPLE_SELECT) {
      const answers = stateValue.map(({ name, id }) => {
        return {
          question_answer_id: id,
          value: name,
        };
      });
      changeAnswers(question_id, answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValue, textFieldOpen, isMobile, fieldValue, multiRating, oneRating]);

  const handleChange = ({ value }: { value: string | null }) => {
    if (type === QuestionTypeEnum.MULTIPLE_AND_SELF) {
      if (stateValue && value) {
        setStateValue((prevState) => [
          ...prevState.filter(({ id }) => id),
          { name: value, id: null },
        ]);
      } else if (!stateValue && value) {
        setStateValue([{ name: value, id: null }]);
      } else if (stateValue && !value) {
        const prevStateValue = stateValue.filter(({ id }) => id);
        !prevStateValue.length
          ? setStateValue(null)
          : setStateValue([...prevStateValue]);
      }
    } else if (type === QuestionTypeEnum.SINGLE_AND_SELF) {
      value ? setStateValue([{ name: value, id: null }]) : setStateValue(null);
    }

    setFieldValue(value);
  };

  const onChangeField: (object: { checked: boolean }) => void = ({
    checked,
  }) => {
    if (type === QuestionTypeEnum.SINGLE_AND_SELF) {
      if (checked && fieldValue) {
        setStateValue([{ name: fieldValue, id: null }]);
      } else if (isMobile) {
        setStateValue([{ name: 'singleSelfChoice', id: null }]);
      }
    }
    if (type === QuestionTypeEnum.MULTIPLE_AND_SELF) {
      if (!checked && fieldValue && stateValue) {
        if (isMobile) {
          setTextFieldOpen(false);
        }
        setStateValue((prevState) => [...prevState.filter(({ id }) => id)]);
      } else if (checked && fieldValue && stateValue) {
        if (isMobile) {
          setTextFieldOpen(true);
        }
        setStateValue((prevState) => [
          ...prevState,
          { name: fieldValue, id: null },
        ]);
      } else if (fieldValue) {
        if (isMobile) {
          setTextFieldOpen(true);
        }
        setStateValue([{ name: fieldValue, id: null }]);
      } else if (isMobile) {
        setTextFieldOpen((prevState) => !prevState);
      }
    }
  };

  const changeFieldsGroup = ({ value, name }: ITextField) => {
    const newArr = stateValue.map((item) => {
      if (item.id === Number(name)) {
        return { ...item, name: value };
      }
      return item;
    });
    setStateValue(newArr);
  };

  const findValueFieldsGroup = (currentId: number) => {
    if (stateValue) {
      let value: string;
      stateValue.forEach(({ id, name }) => {
        if (currentId === id) {
          value = name;
        }
      });
      return value;
    }
  };

  let displayAnswers;

  const isSingleSelfChoice: boolean =
    type === QuestionTypeEnum.SINGLE_AND_SELF &&
    stateValue !== null &&
    ((stateValue[0].name === fieldValue && !stateValue[0].id) ||
      (isMobile && stateValue[0].name === TakingSurveyEnum.SINGLE_SELF_CHOICE));

  const isMultipleSelfChoice: boolean =
    (textFieldOpen && isMobile) ||
    (fieldValue !== null &&
      stateValue !== null &&
      stateValue.filter(({ name, id }) => name === fieldValue && !id).length !==
        0);

  switch (type) {
    case QuestionTypeEnum.SINGLE_SELECT: {
      displayAnswers = (
        <CustomRadioGroup
          className={cx('Answers__group')}
          items={answers}
          value={stateValue ? stateValue[0] : null}
          getKey={({ id }) => id}
          getLabel={({ name }) => name}
          direction="column"
          onChange={({ value }) => setStateValue([value])}
          size="m"
        />
      );
      break;
    }
    case QuestionTypeEnum.SINGLE_AND_SELF: {
      displayAnswers = (
        <>
          <CustomRadioGroup
            className={cx('Answers__group')}
            items={answers}
            value={stateValue && stateValue[0].id ? stateValue[0] : null}
            getLabel={({ name }) => name}
            direction="column"
            getKey={({ id }) => id}
            onChange={({ value }) => setStateValue([value])}
            size="m"
          />
          <div className={cx('Answers__fieldContainer')}>
            <Radio
              checked={isSingleSelfChoice}
              onChange={onChangeField}
              label={isMobile ? 'Свой вариант ответа' : null}
            />
            {(isSingleSelfChoice || !isMobile) && (
              <TextField
                className={cx('Answers__field')}
                onChange={handleChange}
                value={fieldValue}
                placeholder={isMobile ? null : 'Свой вариант ответа'}
                type={isMobile ? 'textarea' : 'text'}
                width="full"
                minRows={isMobile ? 3 : null}
              />
            )}
          </div>
        </>
      );
      break;
    }
    case QuestionTypeEnum.MULTIPLE_SELECT: {
      displayAnswers = (
        <CustomCheckboxGroup
          className={cx('Answers__group')}
          items={answers}
          value={stateValue}
          getLabel={({ name }) => name}
          getKey={({ id }) => id}
          direction="column"
          onChange={({ value }) => setStateValue(value)}
          size="m"
        />
      );
      break;
    }
    case QuestionTypeEnum.MULTIPLE_AND_SELF: {
      displayAnswers = (
        <>
          <CustomCheckboxGroup
            className={cx('Answers__group')}
            items={answers}
            value={stateValue && stateValue.filter(({ id }) => id)}
            getLabel={({ name }) => name}
            getKey={({ id }) => id}
            direction="column"
            onChange={({ value }) =>
              setStateValue((prev) =>
                prev && value
                  ? [...value, ...prev.filter(({ id }) => !id)]
                  : prev
                  ? [...prev.filter(({ id }) => !id)]
                  : value,
              )
            }
          />
          <div className={cx('Answers__fieldContainer')}>
            <Checkbox
              checked={isMultipleSelfChoice}
              onChange={onChangeField}
              label={isMobile ? 'Свой вариант ответа' : null}
            />
            {(!isMobile || textFieldOpen) && (
              <TextField
                className={cx('Answers__field')}
                onChange={handleChange}
                value={fieldValue}
                placeholder={isMobile ? null : 'Свой вариант ответа'}
                type={isMobile ? 'textarea' : 'text'}
                width="full"
                minRows={isMobile ? 3 : null}
              />
            )}
          </div>
        </>
      );
      break;
    }
    case QuestionTypeEnum.SELF_SELECT: {
      displayAnswers = (
        <div className={cx('Answers__fieldGroup')}>
          {answers.map(({ id, name }) => {
            return (
              <TextField
                key={id}
                name={`${id}`}
                onChange={changeFieldsGroup}
                value={findValueFieldsGroup(id)}
                placeholder={name}
                type="text"
                width="full"
              />
            );
          })}
        </div>
      );
      break;
    }
    case QuestionTypeEnum.POINT_RATING: {
      displayAnswers = (
        <div className={cx('Answers__fieldGroup')}>
          <div className={cx('Answers__pointGroup')}>
            <ChoiceGroup
              value={oneRating}
              items={Array.from(Array(rating)).map((_, i) => i + 1)}
              getLabel={(item) => item}
              onChange={({ value }) => setOneRating(value)}
              multiple={false}
              size={isMobile ? 'xs' : 'm'}
              name="ChoiceGroup"
              className={cx('Answers__choiseGroup')}
            />
          </div>
        </div>
      );
      break;
    }
    case QuestionTypeEnum.MULTIPLE_POINT_RATING: {
      displayAnswers = (
        <div className={cx('Answers__fieldGroup')}>
          {answers.map(({ id, name }) => (
            <div key={id} className={cx('Answers__multiGroup')}>
              <Text className={cx('Answers__multiGroupText')} size="s">
                {name}
              </Text>
              <ChoiceGroup
                value={multiRating[id].rating}
                items={Array.from(Array(rating)).map((_, i) => i + 1)}
                getLabel={(item) => item}
                onChange={({ value }) =>
                  setMultiRating({
                    ...multiRating,
                    ...{ [id]: { id: id, name: name, rating: value } },
                  })
                }
                size={isMobile ? 'xs' : 'm'}
                multiple={false}
                name="ChoiceMultiGroup"
                className={cx(
                  'Answers__choiseGroup',
                  'Answers__oneChoiseGroup',
                )}
              />
            </div>
          ))}
        </div>
      );
      break;
    }
  }

  return <div className={cx('Answers')}>{displayAnswers}</div>;
};
export default Answers;
